import React, { useEffect, useMemo, useRef, useState } from "react";
import { Clock3, Check, Smartphone } from "lucide-react";


import { apiGet, apiPatch, apiPost, makeUrl } from "../Api/Client.js";
import "../Styles/HomeScreen.css";
import onboardingVideo from "../Onboarding.mp4";
import { Modal } from "../Shared/ScreenComponents.jsx";
import { TaskIdChip } from "../Shared/TaskIdChip.jsx";
import { WithTaskDescription } from "../Shared/TaskDescription.jsx";
import { TaskGitLinks, hasTaskGitLinks, useTaskGitLinks } from "../Shared/TaskGitLinks.jsx";
import { growthRungLabel, hasConfidentRung } from "../Shared/GrowthRoadmapContent.js";
import {
  filterForEmployee,
  findById,
  findDailyStatus,
  formatDate,
  indexById,
  isCompleted,
  isoDate,
  lastDays,
  resolveActiveEmployee,
  thisWeekDays,
} from "../Shared/ScreenUtils.jsx";

// The widgets "+ Add widget" can offer today. Sprint is real data, gated on
// project access (same 403 Sales already hits on /Project/my-projects/) --
// the others are intentionally inert until the real feature exists; "soon"
// just means visible so people know it's coming, not functional yet.
const WIDGET_CATALOG = [
  { key: "sprint", name: "Sprint", desc: "Your active sprints and progress", tag: "live", plugin: "projects" },
  { key: "quick-leave", name: "Quick Add Leave", desc: "Log a leave without opening the Leave page", tag: "soon", plugin: "timeoff" },
  { key: "growth-skills", name: "Growth & Skills", desc: "Ladder rung + RevOps skill comparison", tag: "soon", plugin: "revops" },
];

// The three "Delivery" families (department_rbac.py's own term) whose work
// runs through per-task EOD reports -- the habit chip only means something
// for them, not for Sales/Marketing/HR/Finance who don't file EODs at all.
const EOD_TRACKED_FAMILIES = new Set(["Engineering", "Design"]);

export function HomeScreen({ data, selectedEmployeeId, reload, navigate }) {
  const user = data.me?.user || data.me?.account || data.me || {};
  // Plugin platform: a product the company has switched off has no card here.
  // Every product is on today, so this hides nothing until a company says otherwise.
  const pluginOn = (code) => (data.me?.plugins || {})[code] !== false;
  const linkedEmployee = resolveActiveEmployee(data);
  const employee = findById(data.employees, linkedEmployee?.id || selectedEmployeeId) || linkedEmployee || data.employees?.[0];
  const useWorkspaceScope = !linkedEmployee?.id;
  // "Your tasks" comes straight from the server (?mine=true), not filtered
  // out of the app-wide task list: for anyone with wide access (superuser,
  // HR, Projects reader) that list is the whole company's tasks cut to one
  // page, and their own can sit past it -- a real open task (ERP-1103) never
  // showed on its owner's own home page for exactly that reason. The shared
  // list is only the fallback while this loads.
  const gitLinks = useTaskGitLinks();
  const [myTasks, setMyTasks] = useState(null);
  const loadMyTasks = () => apiGet("/TasksDashboard/WorkItems/?mine=true&page_size=500")
    .then((res) => setMyTasks(Array.isArray(res) ? res : (res?.results || [])))
    .catch(() => setMyTasks(null));
  useEffect(() => { loadMyTasks(); }, [employee?.id]);
  const employeeTasks = useMemo(() => {
    if (myTasks !== null) return myTasks;
    return useWorkspaceScope ? data.tasks || [] : filterForEmployee(data.tasks, employee?.id);
  }, [myTasks, useWorkspaceScope, data.tasks, employee?.id]);
  const pendingTasks = useMemo(() => employeeTasks.filter((task) => !isCompleted(task.status)), [employeeTasks]);

  const [expandedTaskId, setExpandedTaskId] = useState("");
  const [summary, setSummary] = useState("");

  // Setting a due date straight from the row. Clicking the date is its own
  // action; it must not also open the EOD editor, which is what a click on the
  // rest of the row does.
  const [dueEditingId, setDueEditingId] = useState("");
  const [dueError, setDueError] = useState("");
  const saveDue = async (task, value) => {
    setDueError("");
    try {
      await apiPatch(`/Project/tasks/${task.id}/`, { due_at: value || null });
      setDueEditingId("");
      await loadMyTasks();
    } catch (err) {
      setDueError(err?.payload?.detail || err?.message || "Could not set the due date.");
    }
  };

  // Engineering-only, on-demand read of today's GitHub commits matched to a
  // task by its ERP-<id> code (Backend/Apps/Project/commit_task_matching.py).
  // QA and Design don't push code, so there's nothing here for them -- they
  // keep using the EOD textarea below exactly as before. This is a signal
  // next to the task, not a replacement for writing the EOD line yourself.
  const [githubTouched, setGithubTouched] = useState({});
  useEffect(() => {
    if (employee?.department_family !== "Engineering" || !pluginOn("projects")) return;
    apiGet("/Project/my-github-activity-today/")
      .then((res) => setGithubTouched(res?.touched || {}))
      .catch(() => setGithubTouched({}));
  }, [employee?.department_family, employee?.id]);
  const projectMap = useMemo(() => indexById(data.projects), [data.projects]);
  const taskTableRef = useRef(null);

  const [showOnboarding, setShowOnboarding] = useState(false);
  const [acks, setAcks] = useState({
    bounty: false,
    tracking: false,
    culture: false,
    behavior: false,
    communication: false,
  });

  const onboardingCompleteRef = useRef(false);

  // The server's record (profile_payload.home_onboarding_completed) is the only memory of this:
  // nothing is kept in the browser, so the next person on the same computer is asked too, and a
  // failed save leaves the prompt up.
  useEffect(() => {
    if (linkedEmployee && !linkedEmployee.profile_payload?.home_onboarding_completed && !onboardingCompleteRef.current) {
      setShowOnboarding(true);
    }
  }, [linkedEmployee]);

  const allAcksChecked = Object.values(acks).every(Boolean);
  const [onboardingError, setOnboardingError] = useState("");

  const completeHomeOnboarding = async () => {
    if (!allAcksChecked || !employee?.id) return;
    setOnboardingError("");
    try {
      await apiPatch(`/Users/EmployeeProfiles/${employee.id}/patch-payload/`, {
        profile_payload: {
          home_onboarding_completed: true,
          home_onboarding_at: new Date().toISOString(),
        },
      });
    } catch (e) {
      setOnboardingError("Could not save your acknowledgement. Please try again.");
      return;
    }
    onboardingCompleteRef.current = true;
    setShowOnboarding(false);
    reload(["employees", "me"]);
  };


  const expandedTask = pendingTasks.find((task) => String(task.id) === String(expandedTaskId));
  // The task carries its own project_name now — not a join against the
  // privileged project directory (/Project/ProjectWorkspaces/), which most
  // employees correctly can't read. Before access was enforced, everyone could
  // see that list, so the join silently worked for anyone; once real, every
  // task for a non-full-timer fell back to one generic "Intranet" bucket with
  // no way to tell which project it was actually for. projectMap is kept as a
  // fallback for the rare task predating this field.
  const taskProjectName = (task) => task.project_name || projectMap.get(String(task.project))?.name || "Intranet";
  const myEmployeeId = linkedEmployee?.id || selectedEmployeeId;
  const overdueTasks = employeeTasks.filter((task) => {
    if (isCompleted(task.status) || !task.due_at) return false;
    if (useWorkspaceScope && String(task.owner || task.owner_id) !== String(myEmployeeId)) return false;
    const dueDate = new Date(task.due_at);
    if (Number.isNaN(dueDate.getTime())) return false;
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    return dueDate < todayStart;
  }).length;

  useEffect(() => {
    if (!pendingTasks.length) {
      setExpandedTaskId("");
    }
  }, [pendingTasks]);

  const submitEod = async () => {
    const employeeId = expandedTask?.owner || expandedTask?.owner_id || employee?.id;
    if (!employeeId || !summary.trim()) return;
    const today = isoDate(new Date());
    const existing = findDailyStatus(data.dailyStatus || [], employeeId, today);
    const hasTaskEod = existing && expandedTask?.title ? (existing.summary || "").includes(expandedTask.title) : !!existing;
    if (hasTaskEod && !window.confirm(expandedTask?.title ? `EOD for "${expandedTask.title}" has already been submitted today. Do you want to update it?` : "EOD Already Submitted For Today. Do You Want To Resubmit?")) return;
    try {
      const res = await apiPost("/TasksDashboard/DailyStatusEntries/submit/", {
        employee: employeeId,
        summary,
        status_date: today,
        metadata: {
          source: "React Home",
          task_id: expandedTaskId,
          task_name: expandedTask?.title || "",
          project: expandedTask?.project || "",
          project_name: expandedTask?.project_name || projectMap.get(String(expandedTask?.project))?.name || "",
        },
      });
      const msg = res?._created ? "EOD Report Submitted For Today." : "EOD Report Updated For Today.";
      alert(msg);
      // Only a saved report clears the box; a failed save keeps what the person typed.
      setSummary("");
      setExpandedTaskId("");
      reload(["dailyStatus", "tasks", "notifications", "employees"]);
      loadMyTasks();
    } catch (e) {
      alert("Failed To Submit EOD. Please Try Again.");
    }
  };

  // Sales lives and dies by not missing a follow-up; that's what this
  // homepage should lead with for them, the same way engineers get their
  // work front and center. Fetched separately from the app's normal data
  // load, and only for Sales — this hits a purpose-built endpoint that
  // filters and orders on the server, not the CRM's own full-table
  // endpoint (the one already on record for shipping ~37MB per load).
  // Sales by access family (S3b: a company's department may be called anything), with
  // the old name check kept so nobody who saw these cards before stops seeing them.
  const isSales = pluginOn("crm") && (employee?.department_family === "Sales/BA" || (employee?.department_name || "").trim().toLowerCase() === "sales");
  const [followUps, setFollowUps] = useState(null);
  useEffect(() => {
    if (!isSales) { setFollowUps(null); return; }
    let cancelled = false;
    apiGet("/Banao/my-follow-ups/")
      .then((res) => { if (!cancelled) setFollowUps(res); })
      .catch(() => { if (!cancelled) setFollowUps({ overdue: [], upcoming: [], overdue_count: 0, upcoming_count: 0, due_today_count: 0 }); });
    return () => { cancelled = true; };
  }, [isSales]);

  // Reach (the Android calling app) — Sales only, same flag as the Follow-
  // Ups card above. One current build, no version picker: see
  // ReachAppBuild's own docstring for why that's deliberate.
  const [reachBuild, setReachBuild] = useState(null);
  useEffect(() => {
    if (!isSales) { setReachBuild(null); return; }
    let cancelled = false;
    apiGet("/EnterpriseCore/reach-builds/latest/")
      .then((res) => { if (!cancelled) setReachBuild(res); })
      .catch(() => { if (!cancelled) setReachBuild(null); });
    return () => { cancelled = true; };
  }, [isSales]);

  // The newest line of the build's changelog (CI writes one commit subject per
  // line, newest first) — the "what's new" for the one-line update prompt.
  const reachWhatsNew = (changelog) => {
    const first = String(changelog || "").split("\n").map((l) => l.replace(/^[-*\s]+/, "").trim()).find(Boolean) || "";
    return first.length > 140 ? `${first.slice(0, 137)}…` : first;
  };

  const followUpWhen = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const target = new Date(d); target.setHours(0, 0, 0, 0);
    const days = Math.round((target - today) / 86400000);
    if (days === 0) return "Today";
    if (days === 1) return "Tomorrow";
    if (days === -1) return "Yesterday";
    if (days < 0) return `${Math.abs(days)} days overdue`;
    return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
  };

  const hasFollowUps = followUps && (followUps.overdue.length > 0 || followUps.upcoming.length > 0);

  // Ladder chip: only shown when there's a REAL signal to place someone --
  // a manually-set rung, or a title that actually matches this ladder's own
  // wording (hasConfidentRung). Matching on family alone isn't enough: a
  // title that matches nothing (e.g. "Manager") silently resolves to index
  // 0, the same index a genuine "trainee" match would land on, and showing
  // THAT as "Extreme Junior / Trainee" to someone whose title just isn't
  // ladder-shaped is wrong, not a harmless default -- caught live on
  // Bassi's own page. Engineering/QA/Design stay withheld entirely until
  // their titles get the same treatment Sales already got.
  const salesLadderLabel = employee?.department_family === "Sales/BA"
    && hasConfidentRung(employee.department_family, employee.position_title, employee.growth_rung_override)
    ? growthRungLabel(employee.department_family, employee.position_title, employee.growth_rung_override)
    : null;

  // EOD habit chip. Shown even at 0/7, on purpose — the point is to make a
  // missed habit visible so it gets talked about, not to hide it the way a
  // plain zero normally would get hidden elsewhere on this page.
  const showEodChip = EOD_TRACKED_FAMILIES.has(employee?.department_family);
  const eodDaysLogged = useMemo(() => {
    if (!showEodChip || !employee?.id) return 0;
    return lastDays(7).filter((d) => findDailyStatus(data.dailyStatus, employee.id, d.iso)).length;
  }, [showEodChip, employee?.id, data.dailyStatus]);

  // "+ Add widget" -- Sprint is the one real, working widget today (the
  // section this page used to show unconditionally, now opt-in). Since the plugin
  // platform (S7) the choice is saved on the server, per person and company, so it
  // follows you to any device; the old per-browser flag is moved over once.
  const [widgetMenuOpen, setWidgetMenuOpen] = useState(false);
  const widgetMenuRef = useRef(null);
  useEffect(() => {
    if (!widgetMenuOpen) return;
    const onDocClick = (e) => {
      if (widgetMenuRef.current && !widgetMenuRef.current.contains(e.target)) setWidgetMenuOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [widgetMenuOpen]);

  const sprintStorageKey = employee?.id ? `home-widget-sprint-${employee.id}` : null;
  const [sprintWidgetOn, setSprintWidgetOn] = useState(false);
  const [serverLayout, setServerLayout] = useState(null); // [{widget, slot, order}] once loaded
  useEffect(() => {
    let cancelled = false;
    apiGet("/EnterpriseCore/home-layout/")
      .then(async (res) => {
        if (cancelled) return;
        let layout = res?.layout || [];
        // One-time move of the old per-browser Sprint flag onto the server.
        let legacyOn = false;
        try { legacyOn = !!sprintStorageKey && localStorage.getItem(sprintStorageKey) === "true"; } catch (e) { /* no-op */ }
        if (!res?.stored && legacyOn) {
          try {
            const saved = await apiPost("/EnterpriseCore/home-layout/", { layout: [{ widget: "sprint", slot: "main", order: 0 }] });
            layout = saved?.layout || layout;
          } catch (e) { /* keep showing what the browser remembered */ layout = [{ widget: "sprint", slot: "main", order: 0 }]; }
        }
        setServerLayout(layout);
        setSprintWidgetOn(layout.some((w) => w.widget === "sprint"));
      })
      .catch(() => {
        // Server not reachable for layouts: fall back to the browser's own memory.
        try { setSprintWidgetOn(!!sprintStorageKey && localStorage.getItem(sprintStorageKey) === "true"); } catch (e) { /* no-op */ }
      });
    return () => { cancelled = true; };
  }, [sprintStorageKey]);
  const toggleSprintWidget = () => {
    const next = !sprintWidgetOn;
    setSprintWidgetOn(next);
    try { if (sprintStorageKey) localStorage.setItem(sprintStorageKey, String(next)); } catch (e) { /* no-op */ }
    const others = (serverLayout || []).filter((w) => w.widget !== "sprint");
    const layout = next ? [{ widget: "sprint", slot: "main", order: 0 }, ...others] : others;
    setServerLayout(layout);
    apiPost("/EnterpriseCore/home-layout/", { layout }).catch(() => { /* the browser copy still holds it */ });
  };

  const [myProjects, setMyProjects] = useState(null);
  useEffect(() => {
    if (!sprintWidgetOn) { setMyProjects(null); return; }
    let cancelled = false;
    apiGet("/Project/my-projects/")
      .then((res) => { if (!cancelled) setMyProjects(res.projects || []); })
      .catch(() => { if (!cancelled) setMyProjects([]); });
    return () => { cancelled = true; };
  }, [sprintWidgetOn]);

  const mySprints = useMemo(() => {
    const all = (myProjects || []).flatMap((project) => (project.sprints || []).map((sprint) => ({ ...sprint, project_name: project.name })));
    const rank = (sprint) => {
      const pct = sprint.points_total > 0 ? Math.round((sprint.points_done / sprint.points_total) * 100) : 0;
      if (sprint.is_paused) return 2;
      if (pct >= 100) return 3;
      const atRisk = sprint.days_late > 0 || sprint.overdue_task_count > 0;
      return atRisk ? 0 : 1;
    };
    return all.sort((a, b) => rank(a) - rank(b));
  }, [myProjects]);

  // "Request a new widget" -- a free-text note typed inline and submitted on
  // Enter. No ticket/status workflow; it lands in Django admin for a tenant
  // admin to read later, same spirit as the rest of this menu being small.
  const [widgetRequestText, setWidgetRequestText] = useState("");
  const [widgetRequestStatus, setWidgetRequestStatus] = useState("");
  const submitWidgetRequest = async () => {
    const text = widgetRequestText.trim();
    if (!text) return;
    setWidgetRequestStatus("sending");
    try {
      await apiPost("/MainApp/widget-requests/", { text });
      setWidgetRequestText("");
      setWidgetRequestStatus("sent");
    } catch (e) {
      setWidgetRequestStatus("error");
    }
  };

  const weekDays = useMemo(() => thisWeekDays(), []);
  // "This week" reads due-dates, not meetings — no endpoint today aggregates
  // one person's meetings across every project/lead they're on, and this
  // is not the place to fake that with placeholder events.
  const tasksByDay = useMemo(() => {
    const map = {};
    weekDays.forEach((d) => { map[d.iso] = []; });
    employeeTasks.forEach((task) => {
      if (!task.due_at || isCompleted(task.status)) return;
      const iso = isoDate(new Date(task.due_at));
      if (map[iso]) map[iso].push(task);
    });
    return map;
  }, [weekDays, employeeTasks]);

  const todayIso = isoDate(new Date());
  const todaysTasks = tasksByDay[todayIso] || [];

  const openTaskInTable = (task) => {
    if (isCompleted(task.status)) return;
    setExpandedTaskId(String(task.id));
    requestAnimationFrame(() => taskTableRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
  };

  const firstName = (employee?.display_name || "").trim().split(/\s+/)[0] || "there";
  const lastName = (employee?.display_name || "").trim().split(/\s+/).slice(1).join(" ");

  const upcomingLeaves = useMemo(() => {
    const myEmpId = linkedEmployee?.id || selectedEmployeeId;
    const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
    return (data.leaveRequests || [])
      .filter((lr) => String(lr.status).toLowerCase() === "approved" && lr.starts_on && String(lr.employee) === String(myEmpId))
      // "Upcoming" means not over yet — a leave that already ended (ends_on, or
      // starts_on for a single-day leave) is history, not something to act on.
      .filter((lr) => new Date(lr.ends_on || lr.starts_on) >= todayStart)
      .sort((a, b) => new Date(a.starts_on) - new Date(b.starts_on))
      .slice(0, 4);
  }, [data.leaveRequests, linkedEmployee, selectedEmployeeId]);

  return (
    <section className="HomeR">
      <div className="HomeR-Hero">
        <h1 className="HomeR-Hero-Greeting">Hey, <strong>{firstName}{lastName ? ` ${lastName}` : ""}</strong></h1>
        <div className="HomeR-Hero-Meta">
          {employee?.department_name && <span>{employee.department_name}</span>}
          {overdueTasks > 0 && <span className="HomeR-Hero-Chip red">{overdueTasks} overdue</span>}
          {salesLadderLabel && <span className="HomeR-Hero-Chip ladder">Ladder: {salesLadderLabel}</span>}
          {showEodChip && (
            <span
              className={"HomeR-Hero-Chip" + (eodDaysLogged === 0 ? " red" : "")}
              title={eodDaysLogged === 0
                ? "No EOD logged in the last 7 days — talk to your manager or mentor about how to follow your sprint and tasks."
                : `${eodDaysLogged} of the last 7 days logged.`}
            >
              {eodDaysLogged}/7 days logged
            </span>
          )}
        </div>
      </div>

      {/* Only for a rep who is NOT on the newest Reach build (the server decides —
          it also counts "we've never heard from your phone" as behind, so the
          prompt stays up until their install starts reporting a version). On
          the latest build there is nothing here at all. One line: what's new,
          and when, plus the download. */}
      {isSales && reachBuild?.update_needed && (
        <div className="HomeR-ReachUpdate" role="status">
          <span className="HomeR-ReachUpdate-Pulse" aria-hidden="true" />
          <Smartphone size={18} className="HomeR-ReachUpdate-Icon" aria-hidden="true" />
          <span className="HomeR-ReachUpdate-Text" title={reachBuild.changelog || undefined}>
            <strong>Reach update available</strong>
            {reachWhatsNew(reachBuild.changelog) ? ` — ${reachWhatsNew(reachBuild.changelog)}` : ""}
            {reachBuild.built_at ? ` · ${new Date(reachBuild.built_at).toLocaleDateString(undefined, { day: "numeric", month: "short" })}` : ""}
          </span>
          <a
            className="HomeR-ReachUpdate-Button"
            href={makeUrl(reachBuild.download_url)}
            download={`Reach-${reachBuild.version}.apk`}
          >
            Update now
          </a>
        </div>
      )}

      {todaysTasks.length > 0 && (
        <div className="HomeR-Section">
          <div className="HomeR-Section-Head">
            <span className="HomeR-Section-Label">Your day</span>
            <span className="HomeR-Section-Meta">{todaysTasks.length} due today</span>
          </div>
          <div className="HomeR-DayList">
            {todaysTasks.map((task) => (
              <button key={task.id} type="button" className="HomeR-DayRow" onClick={() => openTaskInTable(task)}>
                <span className="HomeR-DayRow-Dot" />
                <span className="HomeR-DayRow-Title">{task.title}</span>
                <span className="HomeR-DayRow-Project">{task.project_name || ""}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {isSales && hasFollowUps && (
        <div className="HomeR-Card HomeR-FollowUps">
          <div className="HomeR-Card-header">
            <span className="HomeR-Card-title"><Clock3 size={18} /> Your Follow-Ups</span>
            {(followUps.overdue_count > 0 || followUps.due_today_count > 0) && (
              <span className="HomeR-FollowUp-Badges">
                {followUps.overdue_count > 0 && (
                  <button type="button" className="HomeR-FollowUp-CountLink" onClick={() => navigate(`/lms/?owner=${employee.id}&overdue=true`)}>
                    {followUps.overdue_count} overdue
                  </button>
                )}
                {followUps.due_today_count > 0 && (
                  <button type="button" className="HomeR-FollowUp-CountLink HomeR-FollowUp-CountLink--today" onClick={() => navigate(`/lms/?owner=${employee.id}`)}>
                    {followUps.due_today_count} due today
                  </button>
                )}
              </span>
            )}
          </div>
          <div className="HomeR-FollowUps-List">
            {followUps.overdue.slice(0, 5).map((lead) => (
              <button key={`o-${lead.id}`} type="button" className="HomeR-FollowUp-Row is-overdue" onClick={() => navigate(`/lms/${lead.id}/`)}>
                <span className="HomeR-FollowUp-Top">
                  <span className="HomeR-FollowUp-Name">{[lead.full_name, lead.company_name].filter(Boolean).join(" — ") || "Untitled lead"}</span>
                  <span className="HomeR-FollowUp-When">{followUpWhen(lead.next_follow_up_at)}</span>
                </span>
                <span className="HomeR-FollowUp-Sub">{lead.stage}{lead.latest_comment ? ` · "${lead.latest_comment.slice(0, 80)}"` : ""}</span>
              </button>
            ))}
            {followUps.overdue_count > 5 && (
              <button
                type="button"
                className="HomeR-FollowUp-MoreLink"
                onClick={() => navigate(`/lms/?owner=${employee.id}&overdue=true`)}
              >
                + {followUps.overdue_count - 5} more overdue
              </button>
            )}
            {followUps.upcoming_count > 0 && (
              <button
                type="button"
                className="HomeR-FollowUp-MoreLink HomeR-FollowUp-UpcomingLink"
                onClick={() => navigate(`/lms/?owner=${employee.id}`)}
              >
                {followUps.upcoming_count} more follow-up{followUps.upcoming_count === 1 ? "" : "s"} due after today → open full pipeline
              </button>
            )}
          </div>
        </div>
      )}

      {upcomingLeaves.length > 0 && (
        <div className="HomeR-LeavesRow">
          <div className="HomeR-Card">
            <div className="HomeR-Card-header"><span className="HomeR-Card-title">My Upcoming Leaves</span></div>
            <div>
              {upcomingLeaves.map((lr) => (
                <div key={lr.id} className="HomeR-Leave-Row">
                  <span className="HomeR-Leave-Dot" />
                  <span className="HomeR-Leave-Range">{formatDate(lr.starts_on)} - {formatDate(lr.ends_on)}</span>
                  <span className="HomeR-Leave-Type">{lr.leave_type || "Leave"}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="HomeR-Section" ref={taskTableRef}>
        <div className="HomeR-Section-Head">
          <span className="HomeR-Section-Label">Your tasks</span>
          <span className="HomeR-Section-Meta">{pendingTasks.length} open</span>
        </div>
        <div className="HomeR-Card">
          {pendingTasks.map((task) => {
            const selected = String(expandedTaskId) === String(task.id);
            return (
              <WithTaskDescription key={task.id} task={task}>{(desc) => (
              <React.Fragment>
                <div className={selected ? "HomeR-TaskRow active" : "HomeR-TaskRow"} onClick={() => setExpandedTaskId(selected ? "" : String(task.id))}>
                  <button type="button" className={selected ? "HomeR-TaskCheck active" : "HomeR-TaskCheck"} onClick={(e) => { e.stopPropagation(); setExpandedTaskId(selected ? "" : String(task.id)); }}>
                    <Check size={12} />
                  </button>
                  <span className="HomeR-TaskName"><TaskIdChip id={task.id} />{desc.pill}{task.title}</span>
                  {githubTouched[task.id] > 0 && !hasTaskGitLinks(gitLinks[task.id]) && (
                    <span className="HomeR-GithubTouched" title="Commits pushed today that reference this task's id">
                      {githubTouched[task.id]} {githubTouched[task.id] === 1 ? "commit" : "commits"} today
                    </span>
                  )}
                  <span className="HomeR-TaskTag">{taskProjectName(task)}</span>
                  {String(dueEditingId) === String(task.id) ? (
                    <input
                      type="date"
                      className="HomeR-TaskDueInput"
                      autoFocus
                      defaultValue={task.due_at ? isoDate(new Date(task.due_at)) : ""}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => saveDue(task, e.target.value)}
                      onBlur={() => setDueEditingId("")}
                    />
                  ) : (
                    <button
                      type="button"
                      className={"HomeR-TaskDueHint" + (task.due_at ? " set" : "")}
                      title={task.due_at ? "Change the due date" : "Set a due date"}
                      onClick={(e) => { e.stopPropagation(); setDueError(""); setDueEditingId(String(task.id)); }}
                    >
                      {task.due_at ? formatDate(task.due_at) : "+ due date"}
                    </button>
                  )}
                </div>
                {dueError && String(dueEditingId) === String(task.id) && <div className="HomeR-DueError">{dueError}</div>}
                {desc.panel}
                <TaskGitLinks links={gitLinks[task.id]} />
                {selected && (
                  <div className="HomeR-InlineEditor">
                    <strong>Update EOD Report For This Task</strong>
                    <textarea value={summary} onChange={(event) => setSummary(event.target.value)} placeholder="What Did You Do Today On This Task?" />
                    <div className="HomeR-EditorActions">
                      <button className="Primary-Button" onClick={submitEod}>Update EOD Report</button>
                      <button className="Outline-Button" onClick={() => { setExpandedTaskId(""); setSummary(""); }}>Cancel</button>
                    </div>
                  </div>
                )}
              </React.Fragment>
              )}</WithTaskDescription>
            );
          })}
          {!pendingTasks.length && <div className="HomeR-Empty">No open tasks right now.</div>}
        </div>
        <div className="HomeR-EodHint">→ this is where you file your EOD, per task. Click a row to update it.</div>
      </div>

      {sprintWidgetOn && pluginOn("projects") && (
        <div className="HomeR-Section">
          <div className="HomeR-Section-Head">
            <span className="HomeR-Section-Label">Your sprint</span>
            <span className="HomeR-Section-Meta">{mySprints.length} open</span>
          </div>
          {mySprints.length > 0 ? (
            <div className="HomeR-SprintList">
              {mySprints.map((sprint) => {
                const pct = sprint.points_total > 0 ? Math.round((sprint.points_done / sprint.points_total) * 100) : 0;
                const atRisk = sprint.days_late > 0 || sprint.overdue_task_count > 0;
                const statusText = sprint.is_paused
                  ? "Paused"
                  : sprint.days_late > 0
                  ? `${sprint.days_late}d past target`
                  : sprint.overdue_task_count > 0
                  ? `${sprint.overdue_task_count} task${sprint.overdue_task_count === 1 ? "" : "s"} overdue`
                  : "On track";
                return (
                  <div key={sprint.id} className="HomeR-SprintRow">
                    <span className={"HomeR-Sprint-Dot" + (atRisk ? " at-risk" : "")} />
                    <span className="HomeR-Sprint-Body">
                      <span className="HomeR-Sprint-Title">{sprint.name}</span>
                      <span className="HomeR-Sprint-Sub">{sprint.project_name} · {sprint.task_done_count}/{sprint.task_count} team tasks</span>
                    </span>
                    <span className="HomeR-Sprint-Pct">{pct}%</span>
                    <span className="HomeR-Sprint-Status">{statusText}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="HomeR-Card HomeR-Empty">{myProjects === null ? "Loading…" : "No sprint assigned right now."}</div>
          )}
        </div>
      )}

      <div className="HomeR-WidgetAdd" ref={widgetMenuRef}>
        <button type="button" className="HomeR-WidgetAdd-Btn" onClick={() => setWidgetMenuOpen((v) => !v)}>+ Add widget</button>
        {widgetMenuOpen && (
          <div className="HomeR-WidgetMenu">
            <div className="HomeR-WidgetMenu-Label">Available widgets</div>
            {!isSales && pluginOn("projects") && (
              <button type="button" className="HomeR-WidgetOpt" onClick={toggleSprintWidget}>
                <span>
                  <span className="HomeR-WidgetOpt-Name">Sprint</span>
                  <span className="HomeR-WidgetOpt-Desc">Your active sprints and progress</span>
                </span>
                <span className="HomeR-WidgetOpt-Tag live">{sprintWidgetOn ? "Added ✓" : "Add"}</span>
              </button>
            )}
            {WIDGET_CATALOG.filter((w) => w.key !== "sprint" && pluginOn(w.plugin)).map((w) => (
              <div key={w.key} className="HomeR-WidgetOpt disabled">
                <span>
                  <span className="HomeR-WidgetOpt-Name">{w.name}</span>
                  <span className="HomeR-WidgetOpt-Desc">{w.desc}</span>
                </span>
                <span className="HomeR-WidgetOpt-Tag soon">Coming soon</span>
              </div>
            ))}
            <div className="HomeR-WidgetOpt HomeR-WidgetRequest">
              <span className="HomeR-WidgetOpt-Name">Request a new widget</span>
              {widgetRequestStatus === "sent" ? (
                <span className="HomeR-WidgetRequest-Sent">Sent — thanks</span>
              ) : (
                <input
                  type="text"
                  placeholder="Type it, press Enter…"
                  value={widgetRequestText}
                  onChange={(e) => { setWidgetRequestText(e.target.value); setWidgetRequestStatus(""); }}
                  onKeyDown={(e) => { if (e.key === "Enter") submitWidgetRequest(); }}
                  disabled={widgetRequestStatus === "sending"}
                />
              )}
            </div>
          </div>
        )}
      </div>

      {showOnboarding && (
        <Modal wide title="Welcome To Banao — Required Onboarding" closable={false}>
          <div style={{ padding: "0 20px 20px" }}>
            <p style={{ color: "var(--text-muted)", fontSize: 14, marginBottom: 16 }}>Please Review Each Section And Acknowledge To Continue.</p>

            <div className="Onboarding-Video-Wrap">
              <video src={onboardingVideo} controls controlsList="nodownload" />
            </div>

            <div className="Onboarding-Section">
              <h3>INTRODUCTION & COMPANY OVERVIEW</h3>

              <label className="Onboarding-Question">
                <input type="checkbox" checked={acks.bounty} onChange={(e) => setAcks({ ...acks, bounty: e.target.checked })} />
                <span>Did You Understand What The Bounty System Is And Why It Is Used?</span>
              </label>

              <label className="Onboarding-Question">
                <input type="checkbox" checked={acks.tracking} onChange={(e) => setAcks({ ...acks, tracking: e.target.checked })} />
                <span>Do You Understand How Work Is Tracked Or Evaluated At Banao?</span>
              </label>

              <label className="Onboarding-Question">
                <input type="checkbox" checked={acks.culture} onChange={(e) => setAcks({ ...acks, culture: e.target.checked })} />
                <span>Do You Understand That Culture Requires Responsibility, Continuous Learning, And Growth?</span>
              </label>

              <label className="Onboarding-Question">
                <input type="checkbox" checked={acks.behavior} onChange={(e) => setAcks({ ...acks, behavior: e.target.checked })} />
                <span>Do You Understand That Irresponsible Behavior Is Unacceptable And May Lead To Consequences?</span>
              </label>

              <label className="Onboarding-Question">
                <input type="checkbox" checked={acks.communication} onChange={(e) => setAcks({ ...acks, communication: e.target.checked })} />
                <span>Do You Clearly Understand Where To Communicate And Where To Update EOD Reports And Leaves (Slack vs Intranet vs GitHub)?</span>
              </label>
            </div>

            {onboardingError && <p role="alert" style={{ color: "var(--red-600)", fontSize: 14, margin: "0 0 12px" }}>{onboardingError}</p>}
            <div className="Onboarding-Actions">
              <button
                className="Primary-Button"
                style={{ minWidth: 280, opacity: allAcksChecked ? 1 : 0.5 }}
                disabled={!allAcksChecked}
                onClick={completeHomeOnboarding}
              >
                I Agree & Continue
              </button>
            </div>
          </div>
        </Modal>
      )}
    </section>
  );
}
